// src/components/CoursesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import StaffSidebar from "../components/StaffSidebar";
import axios from "axios";
import Pagination from "../components/Pagination";
import "../assets/css/staff-dashboard.css";

export default function CoursesPage() {
  const programme = localStorage.getItem("programme") || "";
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const isHOD = role === "hod";

  const token = localStorage.getItem("token");

  const API =
    process.env.NODE_ENV === "production"
      ? process.env.REACT_APP_API_BASE_URL || "https://schools.luth.gov.ng"
      : "http://localhost:3003";

  const PAGE_SIZE = 12;

  // --------------------------------------------------
  // COMMUNITY HEALTH PROGRAMME HANDLING
  // --------------------------------------------------

  const isCommunityHealth =
    programme.trim().toLowerCase() === "community health";

  /*
   * Only Community Health users get the programme filter.
   */
  const programmeOptions = useMemo(() => {
    if (isCommunityHealth) {
      return [
        "All Community Health",
        "Community Health I",
        "Community Health II",
      ];
    }

    return programme ? [programme] : [];
  }, [programme, isCommunityHealth]);

  const [selectedProgramme, setSelectedProgramme] = useState(
    isCommunityHealth ? "All Community Health" : programme,
  );

  /*
   * Reset the selected programme whenever the logged-in
   * programme changes.
   */
  useEffect(() => {
    if (isCommunityHealth) {
      setSelectedProgramme("All Community Health");
    } else {
      setSelectedProgramme(programme);
    }
  }, [programme, isCommunityHealth]);

  /*
   * Determine which programme(s) should be sent to the API.
   *
   * Community Health:
   * - All Community Health => Community Health I + II
   * - Community Health I    => Community Health I
   * - Community Health II   => Community Health II
   *
   * Other programmes:
   * - Their own programme only
   */
  const programmesToFetch = useMemo(() => {
    if (!programme) return [];

    if (!isCommunityHealth) {
      return [programme];
    }

    if (selectedProgramme === "Community Health I") {
      return ["Community Health I"];
    }

    if (selectedProgramme === "Community Health II") {
      return ["Community Health II"];
    }

    return ["Community Health I", "Community Health II"];
  }, [programme, isCommunityHealth, selectedProgramme]);

  // --------------------------------------------------
  // STATE
  // --------------------------------------------------

  const [courses, setCourses] = useState([]);
  const [levels, setLevels] = useState(["All"]);

  const [courseSearch, setCourseSearch] = useState("");

  const [courseLevel, setCourseLevel] = useState("All");

  const [coursePage, setCoursePage] = useState(1);

  const [loading, setLoading] = useState(false);

  const [savingCourseId, setSavingCourseId] = useState(null);

  const [editingCourseId, setEditingCourseId] = useState(null);

  const [editForm, setEditForm] = useState({
    CourseTitle: "",
    CreditUnits: "",
  });

  // --------------------------------------------------
  // AUTH HEADERS
  // --------------------------------------------------

  const headers = useMemo(() => {
    return token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : undefined;
  }, [token]);

  // --------------------------------------------------
  // LOAD LEVELS
  // --------------------------------------------------

  const loadLevels = async () => {
    if (!programmesToFetch.length) return;

    try {
      const responses = await Promise.all(
        programmesToFetch.map((prog) =>
          axios.get(`${API}/api/staff/courses`, {
            params: {
              programme: prog,
            },
            headers,
          }),
        ),
      );

      const allRows = responses.reduce((all, response) => {
        const rows = Array.isArray(response.data) ? response.data : [];

        return [...all, ...rows];
      }, []);

      const mapped = allRows
        .map((item) =>
          typeof item === "string"
            ? item
            : item?.Level || item?.level || item?.CourseLevel || "",
        )
        .map((lvl) => String(lvl).trim())
        .filter(Boolean);

      setLevels(["All", ...new Set(mapped)]);
    } catch (err) {
      console.error("Failed to fetch course levels:", err);

      setLevels(["All"]);
    }
  };

  // --------------------------------------------------
  // LOAD COURSES
  // --------------------------------------------------

  const loadCourses = async () => {
    if (!programmesToFetch.length) return;

    try {
      setLoading(true);

      const responses = await Promise.all(
        programmesToFetch.map((prog) =>
          axios.get(`${API}/api/staff/courses`, {
            params: {
              programme: prog,
              level: courseLevel === "All" ? "" : courseLevel,
              search: courseSearch.trim(),
            },
            headers,
          }),
        ),
      );

      const allRows = responses.reduce((all, response) => {
        const rows = Array.isArray(response.data) ? response.data : [];

        return [...all, ...rows];
      }, []);

      /*
       * Remove duplicates by CourseID.
       *
       * This is useful when viewing "All Community Health"
       * and the same course happens to exist in both
       * Community Health I and Community Health II.
       */
      const uniqueCourses = Array.from(
        new Map(allRows.map((course) => [course.CourseID, course])).values(),
      );

      setCourses(uniqueCourses);

      setCoursePage(1);

      setEditingCourseId(null);

      setEditForm({
        CourseTitle: "",
        CreditUnits: "",
      });
    } catch (err) {
      console.error("Failed to fetch courses:", err);

      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // LOAD LEVELS WHEN PROGRAMME CHANGES
  // --------------------------------------------------

  useEffect(() => {
    loadLevels();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, programmesToFetch, headers]);

  // --------------------------------------------------
  // LOAD COURSES
  // --------------------------------------------------

  useEffect(() => {
    loadCourses();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, programmesToFetch, courseLevel, courseSearch, headers]);

  // --------------------------------------------------
  // PAGINATION
  // --------------------------------------------------

  const totalPages = Math.ceil(courses.length / PAGE_SIZE);

  const pagedCourses = courses.slice(
    (coursePage - 1) * PAGE_SIZE,
    coursePage * PAGE_SIZE,
  );

  // --------------------------------------------------
  // START EDIT
  // --------------------------------------------------

  const startEdit = (course) => {
    if (!isHOD) return;

    setEditingCourseId(course.CourseID);

    setEditForm({
      CourseTitle: course.CourseTitle ?? "",

      CreditUnits:
        course.CreditUnits === null || course.CreditUnits === undefined
          ? ""
          : String(course.CreditUnits),
    });
  };

  // --------------------------------------------------
  // CANCEL EDIT
  // --------------------------------------------------

  const cancelEdit = () => {
    setEditingCourseId(null);

    setEditForm({
      CourseTitle: "",
      CreditUnits: "",
    });
  };

  // --------------------------------------------------
  // SAVE COURSE
  // --------------------------------------------------

  const handleSave = async (courseId) => {
    if (!isHOD) return;

    try {
      setSavingCourseId(courseId);

      /*
       * For Community Health, use the actual selected
       * programme when saving.
       *
       * If "All Community Health" is selected, determine
       * the programme from the course record itself.
       */
      const courseBeingEdited = courses.find(
        (course) => course.CourseID === courseId,
      );

      let saveProgramme = programme;

      if (isCommunityHealth) {
        if (selectedProgramme === "Community Health I") {
          saveProgramme = "Community Health I";
        } else if (selectedProgramme === "Community Health II") {
          saveProgramme = "Community Health II";
        } else {
          /*
           * When viewing all Community Health,
           * use the programme attached to the course
           * if the API returns it.
           */
          saveProgramme =
            courseBeingEdited?.programme ||
            courseBeingEdited?.Programme ||
            courseBeingEdited?.program ||
            courseBeingEdited?.Program ||
            "Community Health I";
        }
      }

      await axios.put(
        `${API}/api/staff/courses/${courseId}`,
        {
          programme: saveProgramme,

          CourseTitle: editForm.CourseTitle,

          CreditUnits:
            editForm.CreditUnits === "" ? null : Number(editForm.CreditUnits),
        },
        {
          headers,
        },
      );

      await loadCourses();
    } catch (err) {
      console.error("Failed to update course:", err);

      alert(
        err?.response?.data?.msg ||
          err?.response?.data?.message ||
          "Failed to update course.",
      );
    } finally {
      setSavingCourseId(null);
    }
  };

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------

  return (
    <div className="staff-dashboard" style={{ marginTop: "-130px" }}>
      <StaffSidebar />

      <main>
        <h1>Courses</h1>

        {/* ------------------------------------------ */}
        {/* CURRENT PROGRAMME */}
        {/* ------------------------------------------ */}

        <div
          style={{
            marginBottom: "1rem",
            fontWeight: 600,
          }}
        >
          Programme:{" "}
          <span>
            {isCommunityHealth ? selectedProgramme : programme || "N/A"}
          </span>
        </div>

        <section className="panel">
          {/* ---------------------------------------- */}
          {/* CONTROLS */}
          {/* ---------------------------------------- */}

          <div className="controls">
            {/* Community Health Filter */}
            {isCommunityHealth && (
              <select
                value={selectedProgramme}
                onChange={(e) => {
                  setSelectedProgramme(e.target.value);

                  setCoursePage(1);
                  setCourseLevel("All");
                }}
              >
                {programmeOptions.map((prog) => (
                  <option key={prog} value={prog}>
                    {prog}
                  </option>
                ))}
              </select>
            )}

            {/* Search */}
            <input
              type="search"
              placeholder="Search courses…"
              value={courseSearch}
              onChange={(e) => setCourseSearch(e.target.value)}
            />

            {/* Level */}
            <select
              value={courseLevel}
              onChange={(e) => setCourseLevel(e.target.value)}
            >
              {levels.map((lvl) => (
                <option key={lvl} value={lvl}>
                  {lvl}
                </option>
              ))}
            </select>
          </div>

          {/* ---------------------------------------- */}
          {/* NON-HOD NOTICE */}
          {/* ---------------------------------------- */}

          {!isHOD && (
            <div
              style={{
                marginBottom: "1rem",
                padding: "10px 12px",
                borderRadius: 8,
                background: "#fff7e6",
                color: "#8a6d3b",
                fontWeight: 600,
              }}
            >
              You can view courses, but only HODs can edit course titles and
              credit units.
            </div>
          )}

          {/* ---------------------------------------- */}
          {/* COURSE TABLE */}
          {/* ---------------------------------------- */}

          <table className="table modern">
            <thead>
              <tr>
                <th>Code</th>
                <th>Title</th>
                <th>Level</th>
                <th>Units</th>
                <th style={{ width: 170 }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {pagedCourses.map((c) => {
                const isEditing = editingCourseId === c.CourseID;

                return (
                  <tr key={c.CourseID}>
                    {/* CODE */}
                    <td>{c.CourseCode}</td>

                    {/* TITLE */}
                    <td>
                      {isEditing ? (
                        <input
                          type="text"
                          className="course-edit-input"
                          value={editForm.CourseTitle}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              CourseTitle: e.target.value,
                            }))
                          }
                        />
                      ) : (
                        c.CourseTitle
                      )}
                    </td>

                    {/* LEVEL */}
                    <td>{c.Level}</td>

                    {/* CREDIT UNITS */}
                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          className="course-edit-input course-edit-input-number"
                          value={editForm.CreditUnits}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              CreditUnits: e.target.value,
                            }))
                          }
                        />
                      ) : (
                        (c.CreditUnits ?? "-")
                      )}
                    </td>

                    {/* ACTION */}
                    <td>
                      {isHOD ? (
                        isEditing ? (
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                            }}
                          >
                            {/* SAVE */}
                            <button
                              type="button"
                              onClick={() => handleSave(c.CourseID)}
                              disabled={savingCourseId === c.CourseID}
                              className="course-action-btn course-save-btn"
                            >
                              {savingCourseId === c.CourseID
                                ? "Saving..."
                                : "Save"}
                            </button>

                            {/* CANCEL */}
                            <button
                              type="button"
                              onClick={cancelEdit}
                              disabled={savingCourseId === c.CourseID}
                              className="course-action-btn course-cancel-btn"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => startEdit(c)}
                            className="course-action-btn course-edit-btn"
                          >
                            Edit
                          </button>
                        )
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                );
              })}

              {/* NO COURSES */}
              {pagedCourses.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      textAlign: "center",
                      padding: "1rem",
                    }}
                  >
                    {loading ? "Loading courses..." : "No courses found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* ---------------------------------------- */}
          {/* PAGINATION */}
          {/* ---------------------------------------- */}

          <Pagination
            page={coursePage}
            total={totalPages}
            onChange={setCoursePage}
          />
        </section>
      </main>
    </div>
  );
}
